import { z } from 'zod';

import { LIMITS } from './limits';

/** UUID identifiers (PostgreSQL `uuidv7()`). */
export const idSchema = z.uuid();
export type Id = z.infer<typeof idSchema>;

/** Calendar date without time, e.g. a due date: `YYYY-MM-DD` and a real date (NFR-9). */
export const calendarDateSchema = z.iso.date();
export type CalendarDate = z.infer<typeof calendarDateSchema>;

/** An instant in UTC, ISO 8601 with `Z`. */
export const instantSchema = z.iso.datetime({ offset: false });
export type Instant = z.infer<typeof instantSchema>;

/** Emails are trimmed and lower-cased before validation, so lookups are case-insensitive. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(LIMITS.emailMax)
  .pipe(z.email({ message: 'Enter a valid email address.' }));

/** Lower-case hex color, e.g. `#00c875` (labels, groups). */
export const colorSchema = z.string().regex(/^#[0-9a-f]{6}$/, 'Use a hex color like #00c875.');

/** Human-readable task key, e.g. `WED-123` (D27). */
export const TASK_KEY_PATTERN = /^[A-Z]{2,6}-[1-9][0-9]*$/;
export const taskKeySchema = z.string().regex(TASK_KEY_PATTERN);

/** IANA timezone supported by the runtime, e.g. `Asia/Dubai`, `Asia/Damascus`. */
const supportedTimezones = new Set(Intl.supportedValuesOf('timeZone'));
export const timezoneSchema = z
  .string()
  .refine((value) => supportedTimezones.has(value), { message: 'Unknown timezone.' });

/** Cursor pagination query (api-design §1). */
export const cursorQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(LIMITS.pageSizeDefault),
  cursor: z.string().min(1).max(200).optional(),
});
export type CursorQuery = z.infer<typeof cursorQuerySchema>;

/** Response envelope for cursor-paginated collections. */
export function paginatedSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}
